/**
 * Phase 6 Plan 02 — pure filter + sort logic for the HistorySection timeline.
 *
 * No React imports. Tested via vitest unit tests; consumed by HistorySection
 * via useMemo. Conforms to HIST-5.4 (sort), HIST-5.5 (10 filter dimensions),
 * HIST-5.6 (conflict-twin no-grouping) verbatim.
 */
import type {
  ObservedCoatingValue,
  InspectionRead,
} from "../../../api/inspections";

/** Three sort keys exposed by HistoryHeader (D-132). */
export type SortKey = "date_desc" | "date_asc" | "worker_alpha";

/** Tab 1 / Tab 2 boolean field names used by the tick filters. */
export type Tab1Field =
  | "tab1_cleaned"
  | "tab1_structure_damaged"
  | "tab1_cover_needs_level_adjustment"
  | "tab1_pipe_blocked";

export type Tab2Field =
  | "tab2_adjusted_cover_level"
  | "tab2_repaired_internal_structure"
  | "tab2_applied_coating";

/** Coating choice — null is the "no coating" sentinel. */
export type CoatingFilter = "none" | "bitumen" | "epoxy";

/**
 * 10 filter dimensions per HIST-5.5 verbatim:
 *  1. by-worker  (worker_ids: Set<number>)
 *  2. date_from  (ISO date YYYY-MM-DD; inclusive)
 *  3. date_to    (ISO date YYYY-MM-DD; inclusive)
 *  4. tab1_ticks (Set<Tab1Field>; AND semantics — every tick in the set must be true)
 *  5. tab2_ticks (Set<Tab2Field>; AND semantics)
 *  6. coatings   (Set<CoatingFilter>; OR semantics inside the dimension)
 *  7. pm_edited_only (boolean; pm_edit_count > 0)
 *  8. has_photos (boolean; photo_count > 0)
 *  9. no_photos  (boolean; photo_count == 0)
 * 10. (reserved — applied_coating_type, future use)
 */
export interface HistoryFilters {
  worker_ids: Set<number>;
  date_from: string | null;
  date_to: string | null;
  tab1_ticks: Set<Tab1Field>;
  tab2_ticks: Set<Tab2Field>;
  coatings: Set<CoatingFilter>;
  pm_edited_only: boolean;
  has_photos: boolean;
  no_photos: boolean;
}

export const EMPTY_FILTERS: HistoryFilters = {
  worker_ids: new Set(),
  date_from: null,
  date_to: null,
  tab1_ticks: new Set(),
  tab2_ticks: new Set(),
  coatings: new Set(),
  pm_edited_only: false,
  has_photos: false,
  no_photos: false,
};

/** True if at least one filter dimension is non-default. */
export function hasActive(f: HistoryFilters): boolean {
  return (
    f.worker_ids.size > 0 ||
    f.date_from !== null ||
    f.date_to !== null ||
    f.tab1_ticks.size > 0 ||
    f.tab2_ticks.size > 0 ||
    f.coatings.size > 0 ||
    f.pm_edited_only ||
    f.has_photos ||
    f.no_photos
  );
}

/** Reset a single dimension to its EMPTY_FILTERS default. */
export function removeKey<K extends keyof HistoryFilters>(
  f: HistoryFilters,
  key: K,
): HistoryFilters {
  return { ...f, [key]: EMPTY_FILTERS[key] } as HistoryFilters;
}

function coatingFilterMatch(
  set: Set<CoatingFilter>,
  value: ObservedCoatingValue,
): boolean {
  if (set.size === 0) return true;
  const choice: CoatingFilter = value === null ? "none" : value;
  return set.has(choice);
}

function dateBound(iso: string | null, kind: "from" | "to", saved_at: string): boolean {
  if (iso === null) return true;
  // saved_at is full ISO 8601; iso is YYYY-MM-DD. Compare on the day boundary.
  const savedDay = saved_at.slice(0, 10);
  if (kind === "from") return savedDay >= iso;
  // 'to' is inclusive.
  return savedDay <= iso;
}

function passesAllFilters(insp: InspectionRead, f: HistoryFilters): boolean {
  // worker
  if (f.worker_ids.size > 0 && !f.worker_ids.has(insp.worker_id)) return false;
  // date range
  if (!dateBound(f.date_from, "from", insp.saved_at)) return false;
  if (!dateBound(f.date_to, "to", insp.saved_at)) return false;
  // tab1 ticks (AND semantics inside the dimension)
  for (const k of f.tab1_ticks) {
    if (insp[k] !== true) return false;
  }
  // tab2 ticks (AND semantics)
  for (const k of f.tab2_ticks) {
    if (insp[k] !== true) return false;
  }
  // coating (OR semantics)
  if (!coatingFilterMatch(f.coatings, insp.tab1_coating)) return false;
  // pm-edited-only
  if (f.pm_edited_only && insp.pm_edit_count <= 0) return false;
  // has-photos
  if (f.has_photos && insp.photo_count <= 0) return false;
  // no-photos
  if (f.no_photos && insp.photo_count > 0) return false;
  return true;
}

function compareSort(a: InspectionRead, b: InspectionRead, sort: SortKey): number {
  if (sort === "date_desc") {
    if (a.saved_at < b.saved_at) return 1;
    if (a.saved_at > b.saved_at) return -1;
    return 0;
  }
  if (sort === "date_asc") {
    if (a.saved_at < b.saved_at) return -1;
    if (a.saved_at > b.saved_at) return 1;
    return 0;
  }
  // worker_alpha — locale-insensitive ASCII fallback for stable test results.
  const an = a.worker_name ?? "";
  const bn = b.worker_name ?? "";
  return an.localeCompare(bn);
}

/**
 * Filter + sort the inspection list. AND combinator across dimensions; OR
 * within a single multi-select dimension (worker_ids, coatings).
 *
 * Conflict-twin invariant (HIST-5.6): two inspections with the same asset_id
 * and saved_at within 1 minute appear as TWO separate rows in the output —
 * no grouping bracket, no superseded marker. This function does NO row
 * collapsing; both rows pass through if they pass every filter.
 */
export function filterAndSort(
  data: InspectionRead[],
  filters: HistoryFilters,
  sort: SortKey,
): InspectionRead[] {
  const filtered = data.filter((insp) => passesAllFilters(insp, filters));
  const sorted = [...filtered].sort((a, b) => compareSort(a, b, sort));
  return sorted;
}
